<script setup lang="ts">
/**
 * Global toast viewport. Mount once in the app shell (App.vue).
 *
 * - mobile: stacked at the top of the screen, respecting safe-area
 * - desktop: bottom-right corner
 *
 * Reka's swipe-to-dismiss works on touch out of the box.
 */
import {
  ToastProvider,
  ToastRoot,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastViewport,
} from 'reka-ui'
import { X } from '@lucide/vue'
import { computed } from 'vue'

import { useToast, type ToastVariant } from './useToast'
import { isOnline, isSlow } from '@/shared/lib/network'
import { cn } from '@/shared/lib/cn'

const { toasts, dismiss } = useToast()

// The connectivity banner (AppNetworkBanner) pins to top-0 at a higher
// z-index. On mobile the toast viewport is also top-anchored, so when
// the banner is up a top toast renders UNDER it — exactly when errors
// (offline save) are most likely. Push the viewport's top padding down
// past the banner whenever it's visible. Desktop toasts are bottom-
// right (no overlap), where the extra top padding is harmless.
const bannerVisible = computed(() => !isOnline.value || isSlow.value)
const viewportTopPad = computed(() =>
  bannerVisible.value
    ? 'pt-[calc(max(1rem,env(safe-area-inset-top))+3rem)]'
    : 'pt-[max(1rem,env(safe-area-inset-top))]',
)

const variantClasses: Record<ToastVariant, string> = {
  default: 'bg-card text-card-foreground border-border',
  success: 'bg-card text-card-foreground border-primary',
  error: 'bg-destructive text-destructive-foreground border-destructive',
  warning: 'bg-card text-card-foreground border-yellow-500',
}
</script>

<template>
  <ToastProvider swipe-direction="right">
    <ToastRoot
      v-for="t in toasts"
      :key="t.id"
      :duration="t.duration"
      :class="
        cn(
          'pointer-events-auto flex w-full items-start gap-3 rounded-md border p-3 shadow-md',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[swipe=move]:translate-x-(--reka-toast-swipe-move-x)',
          'data-[swipe=end]:animate-out',
          variantClasses[t.variant],
        )
      "
      @update:open="(open) => !open && dismiss(t.id)"
    >
      <div class="flex-1 min-w-0">
        <ToastTitle class="text-sm font-semibold leading-tight">{{ t.title }}</ToastTitle>
        <ToastDescription v-if="t.description" class="mt-1 text-sm opacity-90">
          {{ t.description }}
        </ToastDescription>
      </div>
      <ToastClose
        class="inline-flex size-8 shrink-0 items-center justify-center rounded-md opacity-70 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label="Close notification"
      >
        <X class="size-4" aria-hidden="true" />
      </ToastClose>
    </ToastRoot>
    <!-- The `safe-top`/`safe-bottom` utilities set padding-top/bottom to
         env() which collapses to 0 on desktop and notch-less phones —
         and overrides the `p-4` from the cascade, so the toasts ended
         up flush against the viewport edge. Using arbitrary `max()`
         padding instead guarantees a 1rem gap everywhere and still
         grows past a notch / home indicator. Same fix pattern as the
         site-picker and the bulk action bar. -->
    <ToastViewport
      :class="
        cn(
          'fixed z-60 flex max-w-full flex-col gap-2 outline-none top-0 left-0 right-0 w-full px-4 pb-[max(1rem,env(safe-area-inset-bottom))] md:top-auto md:left-auto md:bottom-0 md:right-0 md:w-96',
          viewportTopPad,
        )
      "
    />
  </ToastProvider>
</template>
